
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CaptchaField from '../../components/base/CaptchaField';

const InvestorRelationsPage = () => {
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const tiers = [
    {
      id: 'jv',
      tier: 'Joint Venture',
      scope: 'Manufacturing',
      commitment: '$5M – $50M',
      example: 'New palm-oil refinery',
      description:
        'Plant-scale production facilities with full operational control and profit-sharing arrangements.',
      icon: 'ri-handshake-line',
      benefits: [
        'Direct ownership stake in production facilities',
        'Access to established supply chains',
        'Shared operational expertise',
        'Long-term revenue streams',
      ],
      timeline: '18-36 months to full operation',
      roi: '15-25% annual return',
    },
    {
      id: 'export',
      tier: 'Export Distribution',
      scope: 'Regional Trade Hubs',
      commitment: '$0.5M – $2M',
      example: 'Southeast-Asia logistics',
      description:
        'Distribution network expansion across key markets with established trade relationships.',
      icon: 'ri-ship-line',
      benefits: [
        'Access to five-continent export network',
        'Established logistics partnerships',
        'Market entry support',
        'Trade financing options',
      ],
      timeline: '6-12 months to market entry',
      roi: '12-18% annual return',
    },
    {
      id: 'tech',
      tier: 'Tech Innovation',
      scope: 'SaaS & Agritech',
      commitment: '$1M – $5M',
      example: 'KL Carpool, B2B e-Marketplace',
      description:
        'Digital platform development with scalable technology and recurring revenue models.',
      icon: 'ri-lightbulb-line',
      benefits: [
        'Scalable digital platforms',
        'Recurring revenue models',
        'MSC-status company backing',
        'Technology transfer opportunities',
      ],
      timeline: '12-24 months to market launch',
      roi: '20-35% annual return',
    },
  ];

  const partners = [
    {
      name: 'Affin Bank',
      description:
        'Leading Malaysian financial institution supporting domestic business growth and trade financing',
      logo: 'ri-bank-line',
    },
    {
      name: 'Exim Bank Malaysia',
      description:
        'Strategic financing partner for export ventures and international manufacturing expansion',
      logo: 'ri-bank-card-line',
    },
    {
      name: 'Citibank Singapore',
      description:
        'Global banking partner enabling cross-border investments and international capital flows',
      logo: 'ri-global-line',
    },
  ];

  const partnerTypes = [
    {
      type: 'Active Partner',
      icon: 'ri-user-star-line',
      tagline: 'Lead, Decide, Grow',
      description:
        "Take the driver's seat. As an Active Partner, you gain hands-on involvement in operations, strategy, and decision-making. Ideal for visionary investors who want direct influence over business growth and maximum returns.",
      benefits: [
        'Board-level participation & voting rights',
        'Operational oversight & management access',
        'Strategic decision-making authority',
        'Higher equity stake & profit share',
        'Direct involvement in expansion plans',
      ],
    },
    {
      type: 'Non-Active Partner',
      icon: 'ri-money-dollar-circle-line',
      tagline: 'Invest, Relax, Earn',
      description:
        'Let your money work for you. As a Non-Active Partner, you enjoy capital investment with passive returns — perfect for investors seeking high-yield dividends without day-to-day management responsibilities.',
      benefits: [
        'Passive income with quarterly interim profit sharing',
        'Regular performance & financial reports',
        'Zero operational commitment required',
        'Flexible exit options & liquidity',
        'Portfolio diversification across 6 divisions',
      ],
    },
  ];

  const handleInquiry = (tierId: string) => {
    setSelectedTier(tierId);
    const element = document.getElementById('inquiry-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-0 pb-0">
        <div className="relative w-full min-h-[320px] md:min-h-[380px]">
          <img
            src="https://public.readdy.ai/ai/img_res/cc096a5306a09295fb123be85e1b2614.jpg"
            alt="Investor Relations - Drixem Group Malaysia Investment Opportunities"
            title="Invest With Drixem Group"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="relative z-10 flex items-center justify-center min-h-[320px] md:min-h-[380px] py-20 px-4 sm:px-6">
            <div className="text-center w-full max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-3 sm:mb-4 leading-tight">
                Invest With Confidence
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-2">
                Transparent, tiered{' '}
                <strong>high-yield opportunities</strong> backed by 30+ years of proven expertise and strategic banking
                partnerships
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Partnership Style */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 rounded-full mb-6">
              <i className="ri-hand-coin-line text-primary-600"></i>
              <span className="text-sm text-primary-700 font-medium">Flexible Investment Models</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Choose Our Partnership as Your Shortcut to Success
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you want hands-on control or passive returns — we have the perfect investment structure for you
            </p>
            <div className="mt-4 inline-flex items-center space-x-2 px-5 py-2 bg-accent-50 border border-accent-200 rounded-full">
              <i className="ri-information-line text-accent-600"></i>
              <span className="text-sm text-accent-700 font-medium">
                New investors &amp; partners are limited to involvement in one division/subsidiary company only
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {partnerTypes.map((pt, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl p-10 hover:border-primary-400 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-xl">
                      <i className={`${pt.icon} text-3xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{pt.type}</h3>
                      <p className="text-sm font-semibold text-primary-600">{pt.tagline}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{pt.description}</p>
                  <ul className="space-y-3 mb-8">
                    {pt.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-double-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      const el = document.getElementById('inquiry-form');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    Choose {pt.type}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">Investment Tiers</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pick the sector that excites you — every tier delivers <strong>proven returns</strong> backed by real
              assets
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-16">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Tier</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Scope</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Commitment</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Example Projects</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tiers.map((tier) => (
                    <tr key={tier.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                            <i className={`${tier.icon} text-xl text-primary-600`}></i>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{tier.tier}</div>
                            <div className="text-sm text-gray-500">{tier.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-gray-700">{tier.scope}</td>
                      <td className="px-6 py-6">
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-semibold whitespace-nowrap">
                          {tier.commitment}
                        </span>
                      </td>
                      <td className="px-6 py-6 text-gray-700">{tier.example}</td>
                      <td className="px-6 py-6">
                        <button
                          onClick={() => handleInquiry(tier.id)}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-7
                            00 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                        >
                          Get Started
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {tiers.map((tier) => (
              <div key={tier.id} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-xl mb-6">
                  <i className={`${tier.icon} text-3xl text-primary-600`}></i>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{tier.tier}</h3>

                <p className="text-gray-600 mb-6">{tier.description}</p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Investment Range</span>
                    <span className="text-sm font-semibold text-gray-900">{tier.commitment}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Expected ROI</span>
                    <span className="text-sm font-semibold text-primary-600">{tier.roi}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Timeline</span>
                    <span className="text-sm font-semibold text-gray-900">{tier.timeline}</span>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Benefits</h4>
                  <ul className="space-y-2">
                    {tier.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                          <i className="ri-check-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleInquiry(tier.id)}
                  className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Request Information
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overseas Investors Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-white border-2 border-primary-200 rounded-2xl p-8 md:p-12 shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary-50 rounded-bl-full opacity-60"></div>
            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-global-line text-3xl text-primary-600"></i>
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-gray-900">
                    For Overseas Investors
                  </h2>
                  <p className="text-sm text-primary-600 font-semibold">Seamless Investment Entry Into Malaysia</p>
                </div>
              </div>
              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                For overseas investors, we can arrange the <strong>Malaysia My Second Home (MM2H)</strong> programme and{' '}
                <strong>MIDA (Malaysian Investment Development Authority)</strong> approvals to ease your investment
                entry into Malaysia. Our team will guide you through the entire process, ensuring a smooth and hassle-free
                experience.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                    <i className="ri-home-heart-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">MM2H Programme</h3>
                    <p className="text-sm text-gray-600">
                      Long-term residency visa enabling you to live and invest in Malaysia with ease.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                    <i className="ri-government-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">MIDA Approval</h3>
                    <p className="text-sm text-gray-600">
                      Government-backed investment facilitation with tax incentives and regulatory support.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                    <i className="ri-graduation-cap-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">International Schools</h3>
                    <p className="text-sm text-gray-600">
                      We provide suggestions on top international schools in Malaysia for your children's education.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-school-line text-primary-600"></i>
                  International School Recommendations
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Malaysia is home to world-class international schools offering British, American, Australian, and IB
                  curricula. We can recommend schools near your investment location:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Alice Smith School (KL)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Garden International School</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Marlborough College Malaysia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Epsom College Malaysia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Matrix International School (N. Sembilan)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-gray-700">Raffles American School (Johor)</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Contact Us for Overseas Investment</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="mailto:IR@drixemgroup.com"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 border-2 border-primary-600 text-primary-600 hover:bg-primary-50 font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  <span>IR@drixemgroup.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Strategic Banking Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your investment is backed by <strong>trusted financial institutions</strong> ensuring security, compliance,
              and seamless capital flow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-6">
                  <i className={`${partner.logo} text-4xl text-primary-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{partner.name}</h3>
                <p className="text-gray-600">{partner.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Appointment CTA */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-calendar-check-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Ready to Discuss Your Investment?
          </h2>
          <p className="text-lg text-white/85 mb-8 max-w-2xl mx-auto">
            Book a face-to-face appointment with our investment team at our Seremban HQ, Johor office, or Armanie Prestige
            KLCC
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-white hover:bg-gray-100 text-primary-600 font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-line text-xl"></i>
            <span>Book an Appointment</span>
          </Link>
        </div>
      </section>

      <section id="inquiry-form" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">Start Your Investment Journey</h2>
            <p className="text-xl text-gray-600">
              Submit your details and our investment team will reach out within 48 hours to arrange a cup of tea meeting
            </p>
          </div>

          <InvestorInquiryForm selectedTier={selectedTier} />
        </div>
      </section>

      <Footer />
    </div>
  );
};

const InvestorInquiryForm = ({ selectedTier }: { selectedTier: string | null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    tier: selectedTier || '',
    partnership_type: '',
    investment_range: '',
    message: '',
  });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'captcha'>('idle');

  const handleCaptchaVerified = useCallback((verified: boolean) => {
    setCaptchaVerified(verified);
    if (verified) setSubmitStatus('idle');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaVerified) {
      setSubmitStatus('captcha');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d69umilbiueuoqm1s50g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          tier: '',
          partnership_type: '',
          investment_range: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="investor-inquiry-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="inv-name" className="block text-sm font-semibold text-gray-900 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="inv-name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label htmlFor="inv-email" className="block text-sm font-semibold text-gray-900 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="inv-email"
            name="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="inv-phone" className="block text-sm font-semibold text-gray-900 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="inv-phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>

        <div>
          <label htmlFor="inv-company" className="block text-sm font-semibold text-gray-900 mb-2">
            Company Name
          </label>
          <input
            type="text"
            id="inv-company"
            name="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div>
          <label htmlFor="inv-partnership_type" className="block text-sm font-semibold text-gray-900 mb-2">
            Partnership Type *
          </label>
          <select
            id="inv-partnership_type"
            name="partnership_type"
            value={formData.partnership_type}
            onChange={(e) => setFormData({ ...formData, partnership_type: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          >
            <option value="">Select type</option>
            <option value="Active Partner">Active Partner</option>
            <option value="Non-Active Partner">Non-Active Partner</option>
            <option value="Undecided">Not Sure Yet</option>
          </select>
        </div>

        <div>
          <label htmlFor="inv-tier" className="block text-sm font-semibold text-gray-900 mb-2">
            Investment Tier *
          </label>
          <select
            id="inv-tier"
            name="tier"
            value={formData.tier}
            onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          >
            <option value="">Select a tier</option>
            <option value="jv">Joint Venture Manufacturing</option>
            <option value="export">Export Distribution</option>
            <option value="tech">Tech Innovation</option>
          </select>
        </div>

        <div>
          <label htmlFor="inv-investment_range" className="block text-sm font-semibold text-gray-900 mb-2">
            Investment Range *
          </label>
          <select
            id="inv-investment_range"
            name="investment_range"
            value={formData.investment_range}
            onChange={(e) => setFormData({ ...formData, investment_range: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          >
            <option value="">Select range</option>
            <option value="0.5-2M">$0.5M - $2M</option>
            <option value="1-5M">$1M - $5M</option>
            <option value="5-50M">$5M - $50M</option>
            <option value="50M+">$50M+</option>
          </select>
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="inv-message" className="block text-sm font-semibold text-gray-900 mb-2">
          Message
        </label>
        <textarea
          id="inv-message"
          name="message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={5}
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm resize-none"
          placeholder="Tell us about your investment goals, preferred divisions, and how you'd like to partner..."
        ></textarea>
        <div className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</div>
      </div>

      <CaptchaField onVerified={handleCaptchaVerified} />

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <p className="text-sm text-primary-700">
            Thank you for your interest! Our investment team will contact you within 48 hours with a tailored proposal.
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            There was an error submitting your inquiry. Please try again or WhatsApp us at +6012-2353795.
          </p>
        </div>
      )}

      {submitStatus === 'captcha' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">Please complete the security verification before submitting.</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-8 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Investment Inquiry'}
      </button>
    </form>
  );
};

export default InvestorRelationsPage;
